import React, { useCallback, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalStateContext } from '../context/GlobalStateContext';
import useReadSession from '../hooks/useReadSession';
import { courseBlockReason, fetchStudentAccess } from '../utils/studentAccess';

/**
 * «Read with me» — курс фоникса. Живёт отдельным статическим приложением в
 * public/course и вставлен сюда кадром того же источника.
 *
 * Кадром, а не переписанным на React, намеренно: внутри восемьсот строк прямой
 * работы с DOM, тренажёры на интервалах и собственный service worker для
 * офлайна. Перенос в компоненты дал бы то же поведение при заметно большем
 * риске, а styles.css курса задаёт body, * и глобальные шрифты — в общем
 * документе он переопределил бы стили сайта.
 *
 * Кадр позиционируется fixed от измеренной высоты шапки, а не от .main-content:
 * там padding-top 4rem, тогда как сама шапка занимает около 80 пикселей
 * (py-4 плюс логотип h-12), и верх курса уезжал под неё. Высоту меряем, а не
 * зашиваем, — на узком экране шапка другая.
 */
// Ученик видит курс во весь экран — у курса своя шапка, и кнопка выхода там.
// Админ смотрит курс внутри сайта, с его меню.
const isStudent = () => localStorage.getItem('isAdmin') !== 'true';

export default function ReadCourse() {
  const navigate = useNavigate();
  const { setAuthenticatedStudent } = useContext(GlobalStateContext);
  const [top, setTop] = useState(isStudent() ? 0 : 80);
  const [ready, setReady] = useState(false);
  // Посещения, время и клики — для админки «Активность в курсе»
  const trackFrame = useReadSession();

  useLayoutEffect(() => {
    const measure = () => {
      let h = 0;
      document.querySelectorAll('nav').forEach((n) => {
        const r = n.getBoundingClientRect();
        if (r.height > h) h = r.height;
      });
      setTop(h);
    };
    measure();
    window.addEventListener('resize', measure);
    // Шапка складывается на узких экранах уже после первой отрисовки
    const t = setTimeout(measure, 300);
    return () => { window.removeEventListener('resize', measure); clearTimeout(t); };
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('rememberedUser');
    localStorage.removeItem('adminToken');
    sessionStorage.setItem('justLoggedOut', 'true');
    setAuthenticatedStudent?.(null);
    navigate('/login', { replace: true });
  }, [navigate, setAuthenticatedStudent]);

  // Кнопка «Выйти» в шапке курса присылает сюда сообщение: сам кадр до
  // логина не дотягивается. Выход — тот же, что в меню сайта.
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin || e.data?.type !== 'rv-logout') return;
      logout();
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [logout]);

  // Срок доступа. Ученик мог войти раньше и не выходить месяцами, поэтому
  // проверяем не только при входе, а при каждом открытии курса и при
  // возвращении на вкладку. Срок вышел — выходим и объясняем на странице входа.
  useEffect(() => {
    const id = localStorage.getItem('studentId');
    if (!isStudent() || !id) return undefined;
    let stopped = false;
    const check = async () => {
      try {
        const data = await fetchStudentAccess(id);
        const reason = courseBlockReason(data);
        if (stopped || !reason) return;
        sessionStorage.setItem('loginNotice', JSON.stringify({ kind: reason, until: data?.accessUntil || null }));
        logout();
      } catch { /* нет связи — не выгоняем, проверим в следующий раз */ }
    };
    check();
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { stopped = true; document.removeEventListener('visibilitychange', onVisible); };
  }, [logout]);

  // Прокрутка живёт внутри курса; внешняя страница не должна прокручиваться.
  // Фон страницы — кремовый, как у курса: иначе между кадрами мелькал голубой.
  useEffect(() => {
    const prev = document.body.style.overflow;
    const prevBg = document.body.style.background;
    document.body.style.overflow = 'hidden';
    document.body.style.background = '#FFF8EC';
    return () => { document.body.style.overflow = prev; document.body.style.background = prevBg; };
  }, []);

  return (
    <div style={{ position: 'fixed', top, left: 0, right: 0, bottom: 0, zIndex: 1 }}>
      {/* Пока кадр грузится — знак курса на его же фоне, и он плавно уходит,
          а не сменяется резко. Раньше здесь была строка «Загружаем курс…». */}
      <div
        aria-hidden={ready}
        className="absolute inset-0 flex flex-col items-center justify-center gap-4"
        style={{ background: '#FFF8EC', zIndex: 2, opacity: ready ? 0 : 1, pointerEvents: ready ? 'none' : 'auto', transition: 'opacity .25s ease' }}
      >
        <svg width="56" height="56" viewBox="0 0 48 48" fill="none" aria-label="Загрузка курса">
          <rect x="3" y="7" width="42" height="34" rx="9" fill="#FFD43B" />
          <path d="M24 13v22" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" />
          <path d="M9 15h11a4 4 0 0 1 4 4v16a4 4 0 0 0-4-4H9z" fill="#fff" />
          <path d="M39 15H28a4 4 0 0 0-4 4v16a4 4 0 0 1 4-4h11z" fill="#fff" />
          <circle cx="15" cy="23" r="2.2" fill="#FF4D6D" />
          <circle cx="33" cy="23" r="2.2" fill="#3D7BFF" />
        </svg>
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-amber-200 border-t-amber-500" />
      </div>
      <iframe
        src={isStudent() ? '/course/index.html?embed=student' : '/course/index.html'}
        title="Read with me — курс чтения по-английски"
        onLoad={(e) => { setReady(true); trackFrame(e.currentTarget); }}
        className="w-full h-full border-0 block"
      />
    </div>
  );
}
